import React, {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiX, HiDownload, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import overlayLogoUrl from "../../assets/peakOverlayLogo.png";
import transparentPattern from "../../assets/images/transparent-grid.jpg";
import RadialProgress from "./radialProgressDashboard";

interface DashboardExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  peakScore: number;
  wellnessScore: number;
  totalLoad: number;
  sleepDurationHours: number;
  sleepPercentage: number;
  totalLoadPercentage: number;
}

const EXPORT_WIDTH = 832;
const EXPORT_HEIGHT = 832;

// Peak score color (matching PeakRecoveryProgress)
const getPeakScoreColor = (score: number): string => {
  if (score > 100) return "#D4AF37";
  if (score >= 80) return "#0096c7";
  if (score >= 60) return "#22c55e";
  if (score >= 30) return "#f97316";
  return "#ef4444";
};

const DashboardExportDialog: React.FC<DashboardExportDialogProps> = ({
  isOpen,
  onClose,
  peakScore,
  wellnessScore,
  totalLoad,
  sleepDurationHours,
  sleepPercentage,
  totalLoadPercentage,
}) => {
  const [selectedOverlay, setSelectedOverlay] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  // Load logo as data URL for SVG embedding
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const loadLogo = async () => {
      try {
        const res = await fetch(overlayLogoUrl);
        const blob = await res.blob();
        const data = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        if (!cancelled) setLogoDataUrl(data);
      } catch (e) {
        console.warn("Failed to load logo", e);
      }
    };
    loadLogo();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const sleepText = useMemo(() => {
    let hours = Math.floor(sleepDurationHours);
    let minutes = Math.round((sleepDurationHours % 1) * 60);
    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }, [sleepDurationHours]);

  // --- SVG radial arc helper ---
  const buildRadialArc = (
    cx: number,
    cy: number,
    radius: number,
    percentage: number,
    strokeColor: string,
    strokeWidth: number,
    label: string,
    valueText: string,
    labelSize?: number,
    valueSize?: number,
    labelGap?: number,
  ): string => {
    const circumference = 2 * Math.PI * radius;
    const dashArray = (percentage / 100) * circumference;
    const dashOffset = 0;
    const lSize = labelSize ?? 24;
    const vSize = valueSize ?? 64;
    const gap = labelGap ?? 48;

    return `
      <!-- ${label} radial -->
      <circle cx="${cx}" cy="${cy}" r="${radius}"
        fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="${strokeWidth}" />
      <circle cx="${cx}" cy="${cy}" r="${radius}"
        fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}"
        stroke-dasharray="${dashArray} ${circumference - dashArray}"
        stroke-dashoffset="${dashOffset}"
        stroke-linecap="round"
        transform="rotate(-90 ${cx} ${cy})" />
      <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
        font-family="'Funnel Sans', -apple-system, sans-serif" font-size="${vSize}px"
        fill="#FFFFFF" font-weight="700">${valueText}</text>
      <text x="${cx}" y="${cy + radius + gap}" text-anchor="middle"
        font-family="'Funnel Sans', -apple-system, sans-serif" font-size="${lSize}px"
        fill="rgba(255,255,255,0.7)" font-weight="500">${label}</text>
    `;
  };

  // --- Overlay 1: Peak Score only ---
  const buildOverlay1SVG = useCallback((): string => {
    const W = EXPORT_WIDTH;
    const H = EXPORT_HEIGHT;
    const centerX = W / 2;
    const centerY = H / 2 - 60;
    const radius = 150;
    const peakPct = Math.min((peakScore / 110) * 100, 100);
    const peakColor = getPeakScoreColor(peakScore);
    const logoW = 234;
    const logoH = 47;
    const labelGap = 56; // space-y-7 equivalent

    const content = buildRadialArc(
      centerX,
      centerY,
      radius,
      peakPct,
      peakColor,
      14,
      "Peak Readiness",
      peakScore === 0 ? "^" : `${Math.round(peakScore)}`,
      28,
      96,
      labelGap,
    );

    const labelY = centerY + radius + labelGap + 28; // bottom of label text
    const logoY = labelY + 32; // space-y-4 equivalent gap below label

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  ${content}
  <g transform="translate(${centerX - logoW / 2}, ${logoY})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoW}" height="${logoH}" />` : ""}
  </g>
</svg>`;
  }, [peakScore, logoDataUrl]);

  // --- Overlay 2: All four metrics ---
  const buildOverlay2SVG = useCallback((): string => {
    const W = EXPORT_WIDTH;
    const H = 1040;
    const centerX = W / 2;

    // Top row: Peak large center
    const peakPct = Math.min((peakScore / 110) * 100, 100);
    const peakColor = getPeakScoreColor(peakScore);
    const peakR = 130;
    const peakCY = 220;

    // Bottom row: three smaller radials
    const smallR = 75;
    const rowY = 550;
    const spacing = W / 4;

    const wellnessColor = "#22c55e";
    const loadColor = "rgba(255,255,255,0.7)";
    const sleepColor = "#a855f7";

    const logoW = 234;
    const logoH = 47;

    let content = "";
    content += buildRadialArc(
      centerX,
      peakCY,
      peakR,
      peakPct,
      peakColor,
      12,
      "Peak Readiness",
      peakScore === 0 ? "^" : `${Math.round(peakScore)}`,
      24,
      72,
    );
    content += buildRadialArc(
      spacing,
      rowY,
      smallR,
      wellnessScore,
      wellnessColor,
      8,
      "Wellness",
      wellnessScore === 0 ? "0" : `${Math.round(wellnessScore)}`,
      18,
      40,
    );
    content += buildRadialArc(
      centerX,
      rowY,
      smallR,
      totalLoadPercentage,
      loadColor,
      8,
      "Load",
      totalLoad === 0 ? "0" : `${Math.round(totalLoad)}`,
      18,
      40,
    );
    content += buildRadialArc(
      spacing * 3,
      rowY,
      smallR,
      sleepPercentage,
      sleepColor,
      8,
      "Sleep",
      sleepText,
      18,
      sleepText.length > 4 ? 28 : 40,
    );

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
  ${content}
  <g transform="translate(${centerX - logoW / 2}, ${rowY + smallR + 48 + 18 + 40})">
    ${logoDataUrl ? `<image href="${logoDataUrl}" width="${logoW}" height="${logoH}" />` : ""}
  </g>
</svg>`;
  }, [
    peakScore,
    wellnessScore,
    totalLoad,
    totalLoadPercentage,
    sleepPercentage,
    sleepText,
    logoDataUrl,
  ]);

  const buildSVG = useCallback((): string => {
    return selectedOverlay === 1 ? buildOverlay1SVG() : buildOverlay2SVG();
  }, [selectedOverlay, buildOverlay1SVG, buildOverlay2SVG]);

  const handleDownload = useCallback(async () => {
    try {
      setIsExporting(true);
      const svgContent = buildSVG();
      const blob = new Blob([svgContent], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (e) => reject(e);
        img.src = url;
      });

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      await new Promise((resolve) => setTimeout(resolve, isIOS ? 500 : 100));

      const canvasWidth = img.naturalWidth || EXPORT_WIDTH;
      const canvasHeight = img.naturalHeight || EXPORT_HEIGHT;

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (pngBlob) => {
          if (!pngBlob) throw new Error("Failed to create PNG");
          const dlUrl = URL.createObjectURL(pngBlob);
          const a = document.createElement("a");
          a.href = dlUrl;
          a.download = `dashboard-overlay-${selectedOverlay}-${Date.now()}.png`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(dlUrl);
            URL.revokeObjectURL(url);
          }, 100);
        },
        "image/png",
        1.0,
      );
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [buildSVG, selectedOverlay]);

  const overlayCount = 2;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 bottom-24 sm:bottom-0 overflow-hidden sm:overflow-y-auto">
          <div className="flex h-full sm:min-h-full sm:items-center sm:justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full h-full sm:h-auto sm:max-w-md transform overflow-hidden sm:rounded-lg text-left align-middle shadow-xl transition-all flex flex-col relative"
                style={{
                  backgroundImage: `url(${transparentPattern})`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "cover",
                }}
              >
                {/* Black blur overlay for the entire dialog */}
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                {/* Header */}
                <div className="shrink-0 z-10 bg-base-100 sm:bg-transparent">
                  <div className="flex justify-between items-center p-3 px-6 sm:p-4 sm:px-8">
                    <h3 className="text-lg font-semibold text-base-content sm:text-white font-grotesk">
                      Share Data Overlay
                    </h3>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-base-200 sm:hover:bg-base-100/10 rounded-full transition-colors"
                    >
                      <HiX className="w-5 h-5 text-base-content/60 sm:text-white/70" />
                    </button>
                  </div>
                </div>

                {/* Preview */}
                <div className="flex-1 min-h-0 flex flex-col justify-center px-4 sm:px-8 py-2 sm:py-4 relative z-10">
                  <div className="relative w-full aspect-[4/5] sm:aspect-[1/1.2] max-h-[calc(100vh-10rem)] mx-auto flex items-center justify-center">
                    {/* Preview content */}
                    <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden rounded-xl">
                      {selectedOverlay === 1 ? (
                        /* Overlay 1: Peak Score Only */
                        <div className="flex flex-col items-center justify-center space-y-4 scale-[0.85]">
                          <div className="text-center space-y-7">
                            <RadialProgress
                              value={Math.min((peakScore / 110) * 100, 100)}
                              size="12rem"
                              thickness="12px"
                              color={`text-[${getPeakScoreColor(peakScore)}]`}
                              text={
                                peakScore === 0
                                  ? "^"
                                  : `${Math.round(peakScore)}`
                              }
                              textSize="text-8xl"
                            />
                            <p className="text-white font-grotesk font-semibold text-2xl">
                              Peak Readiness
                            </p>
                          </div>
                          <img
                            src={overlayLogoUrl}
                            alt="Peak"
                            className="w-28 h-auto"
                          />
                        </div>
                      ) : (
                        /* Overlay 2: All four metrics */
                        <div className="flex flex-col items-center justify-center space-y-4 scale-[0.75]">
                          <div className="text-center space-y-7">
                            <RadialProgress
                              value={Math.min((peakScore / 110) * 100, 100)}
                              size="9rem"
                              thickness="9px"
                              color={`text-[${getPeakScoreColor(peakScore)}]`}
                              text={
                                peakScore === 0
                                  ? "^"
                                  : `${Math.round(peakScore)}`
                              }
                              textSize="text-6xl"
                            />
                            <p className="text-white font-grotesk font-semibold text-lg">
                              Peak Readiness
                            </p>
                          </div>
                          <div className="flex justify-center gap-8">
                            <div className="text-center space-y-7">
                              <RadialProgress
                                value={wellnessScore}
                                size="5rem"
                                thickness="4px"
                                color="text-green-500"
                                text={
                                  wellnessScore === 0
                                    ? "0"
                                    : `${Math.round(wellnessScore)}`
                                }
                                textSize="text-2xl"
                              />
                              <p className="text-white font-grotesk text-xs font-bold">
                                Wellness
                              </p>
                            </div>
                            <div className="text-center space-y-7">
                              <RadialProgress
                                value={totalLoadPercentage}
                                size="5rem"
                                thickness="4px"
                                color="text-white/70"
                                text={
                                  totalLoad === 0
                                    ? "0"
                                    : `${Math.round(totalLoad)}`
                                }
                                textSize="text-2xl"
                              />
                              <p className="text-white font-grotesk text-xs font-bold">
                                Load
                              </p>
                            </div>
                            <div className="text-center space-y-7">
                              <RadialProgress
                                value={sleepPercentage}
                                size="5rem"
                                thickness="4px"
                                color="text-purple-500"
                                text={sleepText}
                                textSize="text-sm"
                              />
                              <p className="text-white font-grotesk text-xs font-bold">
                                Sleep
                              </p>
                            </div>
                          </div>
                          <img
                            src={overlayLogoUrl}
                            alt="Peak"
                            className="w-28 h-auto"
                          />
                        </div>
                      )}
                    </div>

                    {/* Navigation arrows */}
                    <button
                      onClick={() =>
                        setSelectedOverlay((p) =>
                          p <= 1 ? overlayCount : p - 1,
                        )
                      }
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full bg-black/40 text-white hover:bg-neutral/60 transition-all"
                    >
                      <HiChevronLeft className="w-8 h-8" />
                    </button>
                    <button
                      onClick={() =>
                        setSelectedOverlay((p) =>
                          p >= overlayCount ? 1 : p + 1,
                        )
                      }
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full bg-black/40 text-white hover:bg-neutral/60 transition-all"
                    >
                      <HiChevronRight className="w-8 h-8" />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 items-center">
                      {Array.from({ length: overlayCount }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedOverlay(i + 1)}
                          className={`rounded-full transition-all ${
                            selectedOverlay === i + 1
                              ? "w-4 h-1.5 bg-base-100"
                              : "w-1.5 h-1.5 bg-base-100/40 hover:bg-base-100/70"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Export button */}
                <div className="shrink-0 p-4 pb-10 sm:pb-4 sm:pt-0 mx-4 z-10">
                  <button
                    onClick={handleDownload}
                    disabled={isExporting}
                    className="w-full flex justify-center items-center space-x-2 bg-base-100 text-base-content px-4 py-3 sm:py-4 rounded-full hover:bg-base-100/90 disabled:opacity-50 font-grotesk"
                  >
                    <HiDownload className="w-4 h-4" />
                    <span className="text-sm !font-medium">
                      {isExporting ? "Exporting..." : "Export Overlay"}
                    </span>
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DashboardExportDialog;
